'use client'

import { useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import toast from 'react-hot-toast'
import { FiMail, FiLock, FiArrowRight, FiZap, FiArrowLeft, FiKey } from 'react-icons/fi'
import { cn } from '@/lib/utils'

type View = 'login' | 'recuperar' | 'cambiar'

export default function LoginPage() {
  const router = useRouter()
  const searchParams = useSearchParams()
  
  // Estados de vista
  const [view, setView] = useState<View>('login')
  
  // Estados de login
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [cargando, setCargando] = useState(false)
  const [redirecting, setRedirecting] = useState(false)
  
  // Estados de recuperación
  const [emailRecuperar, setEmailRecuperar] = useState('')
  const [codigoRecuperacion, setCodigoRecuperacion] = useState('')
  const [nuevaPassword, setNuevaPassword] = useState('')
  const [cargandoRecuperar, setCargandoRecuperar] = useState(false)
  const [codigoEnviado, setCodigoEnviado] = useState(false)
  
  // Estados de focus
  const [emailFocused, setEmailFocused] = useState(false)
  const [passwordFocused, setPasswordFocused] = useState(false)
  const [emailRecuperarFocused, setEmailRecuperarFocused] = useState(false)
  const [codigoFocused, setCodigoFocused] = useState(false)
  const [nuevaPasswordFocused, setNuevaPasswordFocused] = useState(false)

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!email || !password) {
      toast.error('Por favor completa todos los campos')
      return
    }

    setCargando(true)

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      })

      const data = await response.json()

      if (!response.ok) {
        toast.error(data.error || 'Error al iniciar sesión')
        return
      }

      toast.success(`¡Bienvenido, ${data.usuario.nombreCompleto}!`)
      setRedirecting(true)
      
      const redirectTarget = searchParams.get('redirect') || data.usuario.defaultRoute || '/resumen'
      router.push(redirectTarget)
    } catch (error) {
      console.error('Error:', error)
      toast.error('Error de conexión. Intenta nuevamente.')
    } finally {
      setCargando(false)
    }
  }

  const handleSolicitarCodigo = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!emailRecuperar) {
      toast.error('Ingresa tu correo electrónico')
      return
    }

    setCargandoRecuperar(true)

    try {
      const response = await fetch('/api/auth/recuperar-password/solicitar', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: emailRecuperar }),
      })

      const data = await response.json()

      if (!response.ok) {
        toast.error(data.error || 'Error al enviar código')
        return
      }

      toast.success(data.message)
      setCodigoEnviado(true)
      setView('cambiar')
    } catch (error) {
      console.error('Error:', error)
      toast.error('Error de conexión. Intenta nuevamente.')
    } finally {
      setCargandoRecuperar(false)
    }
  }

  const handleCambiarPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!codigoRecuperacion || !nuevaPassword) {
      toast.error('Completa todos los campos')
      return
    }

    if (nuevaPassword.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres')
      return
    }

    setCargandoRecuperar(true)

    try {
      const response = await fetch('/api/auth/recuperar-password/cambiar', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: emailRecuperar,
          codigo: codigoRecuperacion,
          nuevaPassword
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        toast.error(data.error || 'Error al cambiar contraseña')
        return
      }

      toast.success(data.message)
      // Resetear estados
      setEmailRecuperar('')
      setCodigoRecuperacion('')
      setNuevaPassword('')
      setCodigoEnviado(false)
      setView('login')
    } catch (error) {
      console.error('Error:', error)
      toast.error('Error de conexión. Intenta nuevamente.')
    } finally {
      setCargandoRecuperar(false)
    }
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-cyan-50 via-blue-50 to-indigo-100">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-cyan-400/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-400/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-indigo-400/10 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>

      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo & Title */}
          <div className="text-center mb-8 animate-in fade-in slide-in-from-top duration-700">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-3xl shadow-2xl shadow-cyan-500/50 mb-6 transform hover:scale-110 transition-transform duration-300">
              <FiZap className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl font-black bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 bg-clip-text text-transparent mb-2">
              MiAPI.ClOUD
            </h1>
            <p className="text-gray-600 font-medium">Plataforma En Tiempo Real</p>
          </div>

          {/* Card Container con transiciones */}
          <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 animate-in fade-in slide-in-from-bottom duration-700 delay-200 relative overflow-hidden">
            
            {/* Vista Login */}
            <div className={cn(
              "transition-all duration-500 ease-in-out",
              view === 'login' ? 'translate-x-0 opacity-100' : 'absolute -translate-x-full opacity-0 pointer-events-none'
            )}>
              <form onSubmit={handleLogin} className="space-y-6">
                {/* Email Field */}
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl blur-sm transition-opacity duration-300 ${emailFocused ? 'opacity-20' : 'opacity-0'}`}></div>
                  <div className="relative">
                    <FiMail className={`absolute left-4 top-1/2 -translate-y-1/2 transition-all duration-300 ${emailFocused || email ? 'text-cyan-500' : 'text-gray-400'}`} />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onFocus={() => setEmailFocused(true)}
                      onBlur={() => setEmailFocused(false)}
                      className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-gray-200 rounded-2xl outline-none transition-all duration-300 focus:border-cyan-500 focus:bg-white peer"
                      placeholder=" "
                      disabled={cargando}
                      required
                    />
                    <label className={`absolute left-12 transition-all duration-300 pointer-events-none
                      ${emailFocused || email 
                        ? 'top-0 -translate-y-1/2 text-xs bg-white px-2 text-cyan-600 font-semibold' 
                        : 'top-1/2 -translate-y-1/2 text-gray-500'
                      }`}>
                      Correo Electrónico
                    </label>
                  </div>
                </div>

                {/* Password Field */}
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl blur-sm transition-opacity duration-300 ${passwordFocused ? 'opacity-20' : 'opacity-0'}`}></div>
                  <div className="relative">
                    <FiLock className={`absolute left-4 top-1/2 -translate-y-1/2 transition-all duration-300 ${passwordFocused || password ? 'text-blue-500' : 'text-gray-400'}`} />
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onFocus={() => setPasswordFocused(true)}
                      onBlur={() => setPasswordFocused(false)}
                      className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-gray-200 rounded-2xl outline-none transition-all duration-300 focus:border-blue-500 focus:bg-white"
                      placeholder=" "
                      disabled={cargando}
                      required
                    />
                    <label className={`absolute left-12 transition-all duration-300 pointer-events-none
                      ${passwordFocused || password 
                        ? 'top-0 -translate-y-1/2 text-xs bg-white px-2 text-blue-600 font-semibold' 
                        : 'top-1/2 -translate-y-1/2 text-gray-500'
                      }`}>
                      Contraseña
                    </label>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={cargando}
                  className="group relative w-full py-4 bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-600 text-white font-bold rounded-2xl shadow-lg shadow-cyan-500/50 hover:shadow-xl hover:shadow-cyan-500/60 transition-all duration-300 hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="relative flex items-center justify-center gap-2">
                    {cargando ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Iniciando sesión...</span>
                      </>
                    ) : (
                      <>
                        <span>Iniciar Sesión</span>
                        <FiArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                      </>
                    )}
                  </div>
                </button>

                {/* Footer */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-center text-xs text-gray-500 mb-3">
                    Todo el poder de nuestras APIs, Cloud Hosting y Panel de Control con una sola cuenta
                  </p>
                  <button
                    type="button"
                    onClick={() => setView('recuperar')}
                    className="text-sm text-cyan-600 hover:text-cyan-700 font-semibold hover:underline w-full text-center"
                  >
                    ¿Olvidaste tu contraseña? Recupérala aquí
                  </button>
                </div>
              </form>
            </div>

            {/* Vista Recuperar */}
            <div className={cn(
              "transition-all duration-500 ease-in-out",
              view === 'recuperar' ? 'translate-x-0 opacity-100' : view === 'login' ? 'absolute translate-x-full opacity-0 pointer-events-none' : 'absolute -translate-x-full opacity-0 pointer-events-none'
            )}>
              <form onSubmit={handleSolicitarCodigo} className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-800">Recuperar Contraseña</h2>
                  <p className="text-sm text-gray-600 mt-2">Ingresa tu correo para recibir un código</p>
                </div>

                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl blur-sm transition-opacity duration-300 ${emailRecuperarFocused ? 'opacity-20' : 'opacity-0'}`}></div>
                  <div className="relative">
                    <FiMail className={`absolute left-4 top-1/2 -translate-y-1/2 transition-all duration-300 ${emailRecuperarFocused || emailRecuperar ? 'text-cyan-500' : 'text-gray-400'}`} />
                    <input
                      type="email"
                      value={emailRecuperar}
                      onChange={(e) => setEmailRecuperar(e.target.value)}
                      onFocus={() => setEmailRecuperarFocused(true)}
                      onBlur={() => setEmailRecuperarFocused(false)}
                      className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-gray-200 rounded-2xl outline-none transition-all duration-300 focus:border-cyan-500 focus:bg-white"
                      placeholder=" "
                      disabled={cargandoRecuperar}
                      required
                    />
                    <label className={`absolute left-12 transition-all duration-300 pointer-events-none
                      ${emailRecuperarFocused || emailRecuperar 
                        ? 'top-0 -translate-y-1/2 text-xs bg-white px-2 text-cyan-600 font-semibold' 
                        : 'top-1/2 -translate-y-1/2 text-gray-500'
                      }`}>
                      Correo Electrónico
                    </label>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={cargandoRecuperar}
                  className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] disabled:opacity-50"
                >
                  {cargandoRecuperar ? 'Enviando...' : 'Enviar Código'}
                </button>

                <button
                  type="button"
                  onClick={() => setView('login')}
                  className="w-full flex items-center justify-center gap-2 text-gray-600 hover:text-gray-800 font-medium"
                >
                  <FiArrowLeft className="w-4 h-4" />
                  Volver al login
                </button>
              </form>
            </div>

            {/* Vista Cambiar Password */}
            <div className={cn(
              "transition-all duration-500 ease-in-out",
              view === 'cambiar' ? 'translate-x-0 opacity-100' : 'absolute translate-x-full opacity-0 pointer-events-none'
            )}>
              <form onSubmit={handleCambiarPassword} className="space-y-6">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-800">Cambiar Contraseña</h2>
                </div>

                {codigoEnviado && (
                  <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-4 text-sm text-cyan-800">
                    ✉️ Código enviado a <strong>{emailRecuperar}</strong>
                  </div>
                )}

                {/* Email (Readonly) */}
                <div className="relative">
                  <FiMail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    value={emailRecuperar}
                    className="w-full pl-12 pr-4 py-4 bg-gray-100 border-2 border-gray-200 rounded-2xl outline-none cursor-not-allowed"
                    disabled
                    readOnly
                  />
                  <label className="absolute left-12 top-0 -translate-y-1/2 text-xs bg-white px-2 text-gray-500 font-semibold">
                    Correo Electrónico
                  </label>
                </div>

                {/* Código */}
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-2xl blur-sm transition-opacity duration-300 ${codigoFocused ? 'opacity-20' : 'opacity-0'}`}></div>
                  <div className="relative">
                    <FiKey className={`absolute left-4 top-1/2 -translate-y-1/2 transition-all duration-300 ${codigoFocused || codigoRecuperacion ? 'text-cyan-500' : 'text-gray-400'}`} />
                    <input
                      type="text"
                      value={codigoRecuperacion}
                      onChange={(e) => setCodigoRecuperacion(e.target.value.toUpperCase())}
                      onFocus={() => setCodigoFocused(true)}
                      onBlur={() => setCodigoFocused(false)}
                      className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-gray-200 rounded-2xl outline-none transition-all duration-300 focus:border-cyan-500 focus:bg-white uppercase tracking-widest"
                      placeholder=" "
                      maxLength={6}
                      disabled={cargandoRecuperar}
                      required
                    />
                    <label className={`absolute left-12 transition-all duration-300 pointer-events-none
                      ${codigoFocused || codigoRecuperacion 
                        ? 'top-0 -translate-y-1/2 text-xs bg-white px-2 text-cyan-600 font-semibold' 
                        : 'top-1/2 -translate-y-1/2 text-gray-500'
                      }`}>
                      Código de Verificación
                    </label>
                  </div>
                </div>

                {/* Nueva Password */}
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl blur-sm transition-opacity duration-300 ${nuevaPasswordFocused ? 'opacity-20' : 'opacity-0'}`}></div>
                  <div className="relative">
                    <FiLock className={`absolute left-4 top-1/2 -translate-y-1/2 transition-all duration-300 ${nuevaPasswordFocused || nuevaPassword ? 'text-blue-500' : 'text-gray-400'}`} />
                    <input
                      type="password"
                      value={nuevaPassword}
                      onChange={(e) => setNuevaPassword(e.target.value)}
                      onFocus={() => setNuevaPasswordFocused(true)}
                      onBlur={() => setNuevaPasswordFocused(false)}
                      className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-gray-200 rounded-2xl outline-none transition-all duration-300 focus:border-blue-500 focus:bg-white"
                      placeholder=" "
                      disabled={cargandoRecuperar}
                      required
                    />
                    <label className={`absolute left-12 transition-all duration-300 pointer-events-none
                      ${nuevaPasswordFocused || nuevaPassword 
                        ? 'top-0 -translate-y-1/2 text-xs bg-white px-2 text-blue-600 font-semibold' 
                        : 'top-1/2 -translate-y-1/2 text-gray-500'
                      }`}>
                      Nueva Contraseña
                    </label>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={cargandoRecuperar}
                  className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02] disabled:opacity-50"
                >
                  {cargandoRecuperar ? 'Cambiando...' : 'Cambiar Contraseña'}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setView('login')
                    setCodigoEnviado(false)
                  }}
                  className="w-full flex items-center justify-center gap-2 text-gray-600 hover:text-gray-800 font-medium"
                >
                  <FiArrowLeft className="w-4 h-4" />
                  Volver al login
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Loading Overlay durante redirección */}
      {redirecting && (
        <div className="fixed inset-0 bg-gradient-to-br from-cyan-500/20 via-blue-500/20 to-indigo-500/20 backdrop-blur-md z-50 flex items-center justify-center animate-in fade-in duration-300">
          <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl p-8 text-center border border-white/20">
            <div className="relative w-20 h-20 mx-auto mb-4">
              <div className="absolute inset-0 border-4 border-cyan-200 rounded-full"></div>
              <div className="absolute inset-0 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
              <FiZap className="absolute inset-0 m-auto w-8 h-8 text-cyan-600" />
            </div>
            <p className="text-lg font-bold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
              Redirigiendo...
            </p>
            <p className="text-sm text-gray-500 mt-2">Preparando tu panel de control</p>
          </div>
        </div>
      )}
    </div>
  )
}
